import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ChatService } from './chat.service';
import { ChatUiService } from './chat-ui.service';
import { shouldChime } from './ui/chat-sound';

const MUTED_KEY = 'chat.sound.muted';

/**
 * A short two-note chime when a chat message arrives (ported from the referrals dashboard's SoundService): made with
 * Web Audio, so there is no file to download. Which messages chime is shouldChime's rule; on or off is per device.
 * Browsers only allow sound after the page has been touched, so the audio is unlocked on the first tap or key press.
 */
@Injectable({ providedIn: 'root' })
export class ChatSoundService implements OnDestroy {
  readonly enabled$ = new BehaviorSubject<boolean>(!readMuted());
  private context: AudioContext | null = null;
  private lastChimeAt = 0;
  private sub: Subscription | null = null;

  constructor(private readonly chat: ChatService, private readonly ui: ChatUiService) {}

  /** Starts listening; called once from the chat window in the app shell. */
  init(): void {
    if (this.sub) return;
    const unlock = () => {
      this.audio()?.resume().catch(() => undefined);
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);

    this.sub = this.chat.store.incoming$.subscribe((message) => {
      const snapshot = this.chat.store.snapshot;
      const ui = this.ui.snapshot;
      const viewing = ui.open && !ui.minimized && ui.conversationId === message.conversationId
        && document.visibilityState === 'visible' && document.hasFocus();
      const now = Date.now();
      if (!shouldChime({
        conversation: snapshot.conversations[message.conversationId], message, me: snapshot.me?.principalId ?? null,
        now, viewing, soundsOn: this.enabled$.value, lastChimeAt: this.lastChimeAt,
      })) return;
      this.lastChimeAt = now;
      this.play();
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  setEnabled(on: boolean): void {
    this.enabled$.next(on);
    try {
      if (on) localStorage.removeItem(MUTED_KEY);
      else localStorage.setItem(MUTED_KEY, '1');
    } catch {
      // Not remembered in this browser; it applies until the page is reloaded.
    }
    // A sample, so turning it on says what it sounds like.
    if (on) this.play();
  }

  private play(): void {
    const ctx = this.audio();
    if (!ctx) return;
    // Still locked: a browser that already trusts this site lets it resume without a new tap.
    if (ctx.state !== 'running') {
      ctx.resume().then(() => { if (ctx.state === 'running') this.notes(ctx); }, () => undefined);
      return;
    }
    this.notes(ctx);
  }

  private notes(ctx: AudioContext): void {
    const start = ctx.currentTime;
    // Two soft sine notes, E6 then A6.
    for (const [freq, at] of [[1318.5, 0], [1760, 0.12]] as const) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, start + at);
      gain.gain.exponentialRampToValueAtTime(0.18, start + at + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + at + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start + at);
      osc.stop(start + at + 0.4);
    }
  }

  private audio(): AudioContext | null {
    if (this.context) return this.context;
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    try {
      this.context = new Ctor();
    } catch {
      return null;
    }
    return this.context;
  }
}

function readMuted(): boolean {
  try {
    return localStorage.getItem(MUTED_KEY) === '1';
  } catch {
    return false;
  }
}
